import type { HorrorRaceState } from "../types/game-state";
import {
  appendDraw,
  getCurrentDrawPass,
  getCurrentRaceDrawHistory,
} from "../utils/draw-history";
import { isFewPlayerVariant } from "../utils/player-count-rules";

// A forfeit is a taken turn: it advances the turn and completes the pass exactly
// like applyPlaceCoins, so round-end is driven by action count (every player
// acting once per pass) — never by pool exhaustion. Mirrors the place-coins
// turn-advance / few-player pass-1→pass-2 / resolve logic so the two action
// paths cannot diverge.
export function applyForfeitTurn(state: HorrorRaceState): HorrorRaceState {
  const currentPlayerIndex = state.turnOrder[state.currentTurnIndex];

  // M1: record the forfeit on the current race's history with coins=null. The
  // 3 tokens that would have been drawn are unknown to the other players, so
  // they get tallied into the sheet's `?` column. Append BEFORE turn advance
  // so the recorded playerIndex/drawPass matches the forfeiter, not the next
  // player.
  const nextHistory = appendDraw(getCurrentRaceDrawHistory(state), state, {
    playerIndex: currentPlayerIndex,
    drawPass: getCurrentDrawPass(state),
    coins: null,
  });

  const nextTurnIndex = state.currentTurnIndex + 1;
  const passComplete = nextTurnIndex >= state.turnOrder.length;
  const pass = state.drawPass ?? 1;

  const base: HorrorRaceState = {
    ...state,
    forfeitPlayerIndex: currentPlayerIndex,
    hasForfeitThisRound: true,
    // Clear the forfeiting player's draw so the next player draws fresh — the
    // host auto-draw is gated on drawnCoins.length === 0.
    drawnCoins: [],
    currentRaceDrawHistory: nextHistory,
  };

  // ≤5-player games draw twice per round: once pass 1 completes, start pass 2
  // (same round, no resolve yet).
  if (
    passComplete &&
    pass === 1 &&
    isFewPlayerVariant(state.config.playerCount)
  ) {
    return {
      ...base,
      currentTurnIndex: 0,
      drawPass: 2,
    };
  }

  return {
    ...base,
    currentTurnIndex: passComplete ? 0 : nextTurnIndex,
    drawPass: passComplete ? 1 : pass,
    phase: passComplete ? "ROUND_RESOLVE" : state.phase,
  };
}
